import React, { useState } from "react";
import { useAuthStore, selectAuthAdmin } from "../../store/useAuth";
import { usePost } from "../../packages/hooks";
import Icon from "../../components/icon/icon";
import "./settings.css";

// Settings form data interface
interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordVisibility {
  currentPassword: boolean;
  newPassword: boolean;
  confirmPassword: boolean;
}

const Settings: React.FC = () => {
  const currentUser = useAuthStore(selectAuthAdmin);
  const [formData, setFormData] = useState<PasswordFormData>({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState<PasswordVisibility>({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  // Initialize hooks
  const changePasswordMutation = usePost("/auth/change-password", {
    onSuccess: () => {
      // Success handled in the submit function
    },
    onError: () => {
      // Error handled in the submit function
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field: keyof PasswordVisibility): void => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    if (!currentUser) {
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      return;
    }

    if (formData.newPassword.length < 6) {
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      console.error("Error changing password:", error);
    }
  };

  return (
    <div className="settings">
      <div className="settings__header">
        <h1 className="settings__title">Настройки</h1>
      </div>

      <div className="settings__content">
        <div className="settings__section">
          <h2 className="settings__section-title">Профиль</h2>
          <div className="settings__profile">
            <div className="settings__profile-info">
              <p>
                <strong>Email:</strong> {currentUser?.email}
              </p>
              <p>
                <strong>Имя:</strong>{" "}
                {(currentUser as any)?.name || "Не указано"}
              </p>
            </div>
          </div>
        </div>

        <div className="settings__section">
          <h2 className="settings__section-title">Изменить пароль</h2>
          <form onSubmit={handleSubmit} className="settings__password-form">
            <div className="form-group">
              <label htmlFor="currentPassword">Текущий пароль</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.currentPassword ? "text" : "password"}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility("currentPassword")}
                >
                  <Icon
                    name={showPasswords.currentPassword ? "hide" : "view"}
                    size="small"
                  />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">Новый пароль</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.newPassword ? "text" : "password"}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility("newPassword")}
                >
                  <Icon
                    name={showPasswords.newPassword ? "hide" : "view"}
                    size="small"
                  />
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Подтвердите новый пароль</label>
              <div className="password-input-wrapper">
                <input
                  type={showPasswords.confirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  minLength={6}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                >
                  <Icon
                    name={showPasswords.confirmPassword ? "hide" : "view"}
                    size="small"
                  />
                </button>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className={`btn btn--primary ${
                  changePasswordMutation.isPending ? "btn--loading" : ""
                }`}
                disabled={changePasswordMutation.isPending}
              >
                {changePasswordMutation.isPending
                  ? "Изменение..."
                  : "Изменить пароль"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
